import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import { format , parseISO, isValid} from "date-fns";
import moment from 'moment';
//api
import { getProductDetailByType, deleteProduct } from 'api/products';
import { editBookDetail } from 'api/libros';
import { editMerchandising } from 'api/merchandising';
//components
import Title from 'components/backoffice/texts/title';
import ProductForm from 'components/backoffice/forms/productsForm';

const EditProduct = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState({});
  const searchParams = new URLSearchParams(location.search);
  const typeFromUrl = searchParams.get('type');
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [type, setType] = useState(typeFromUrl || '');
  const [errorOnAction, setErrorOnAction] = useState({});
  const { t } = useTranslation(['backoffice']);
  const { setTitle } = useOutletContext();
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!type) return;
    const fetchDetail = async () => {
      try {
        const { data, status } = await getProductDetailByType(id, type);
        if (status === 200) {
          setProduct(data);
        }
      } catch (error) {
        console.error('Error fetching product detail:', error);
      }
    };
    fetchDetail();
  }, [type, id]);

  useEffect(() => {
    if (product) {
      setTitle(t('edit', { title: product?.titulo }));
    }
    return () => {
      setTitle('');
    };
  }, [product, setTitle]);

  const onSubmit = useCallback(async (values) => {
    setLoading(true);
    const data = {
      ean: values.ean,
      titulo: values.titulo,
      titulo_orig: values.titulo_org,
      caratula: values.caratula,
      texto_resumen: values.texto_resumen,
      num_en_coleccion: values.num_en_coleccion === ""? 0 : parseInt(values.num_en_coleccion),
      precio_sin_iva: values.precio_sin_iva,
      precio_venta_publico: values.precio_venta_publico,
      iva: parseInt(values.iva),
      ubicacion_stock: values.ubicacion_stock,
      editorial: values.editorial?.value,
      genero: values.genero,
      publicada: values.publicada,
      ...(values?.cover instanceof File ? { cover: values?.cover } : {}),
      product_type: values?.product_type?.id
    };
    switch (values.product_type.id) {
      case 'book':
        data.alto_cm = values.alto_cm
        data.ancho_cm = values.ancho_cm
        data.grosor_cm = values.grosor_cm
        data.peso = Math.trunc(values.peso)
        data.num_edicion = values.num_edicion
        data.num_paginas = values.num_paginas
        data.ibic = Array.isArray(values.ibic) ? values.ibic.map(item => item.value) : []
        data.encuadernacion = Array.isArray(values.bookbindings) ? values.bookbindings.map(item => item.value) : []
        data.authors = Array.isArray(values.authors) ? values.authors.map(item => item.value) : []
        data.fecha_public =  values.publication_date === '1-01-01' || values.publication_date === null ? moment('2021-01-01').local().format('YYYY-MM-DD') : moment(values.publication_date).local().format('YYYY-MM-DD');
        data.languages = Array.isArray(values.languages) ? values.languages.map(item => item.value) : []
        if (values.availabled_status && typeof values.availabled_status === 'object') {
          const formattedStatus = {};
          Object.entries(values.availabled_status).forEach(([code, hasStock]) => {
            formattedStatus[code] = hasStock === true ? 2 : 1;
          });
          data.availabled_status = formattedStatus;
        }
        break;

      case 'merchandising':
        data.options = JSON.stringify(values.options || []);
        break;
      default:
        break;
    }
    try {
      const response = values.product_type.id === 'book' ? await editBookDetail(id, data) : await editMerchandising(id, data);
      if (response.status === 200) {
        toast.success(t('editadocorrectamente'));
        navigate('/intranet/products', { replace: true });
      } else {
        setErrorOnAction(response.data)
        toast.error(`${t('error')} ${response.status}: ${t('mensajeerror')}`, { theme: 'colored' });
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  }, []);

  const handleDelete = useCallback(async () => {
    setLoading(true);
    try {
      const { status } = await deleteProduct(id);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        navigate('/intranet/products', { replace: true });
      } else {
        toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`, { theme: 'colored' });
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  }, [openModalDelete]);

  return (
    <div className="mt-5">
      <Title text={t('editproduct')} />
      <ProductForm
        loading={loading}
        isEditing={true}
        onSubmitForm={onSubmit}
        openModalDelete={openModalDelete}
        setOpenModalDelete={setOpenModalDelete}
        defaultValues={product}
        onDelete={handleDelete}
        fileInputRef={fileInputRef}
        id={id}
        errorOnAction={errorOnAction}
      />
    </div>
  );
};

export default EditProduct;